// src/hooks/useNotifications.js
import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import * as notificationsService from '../services/notificationsService';
import { errorDev } from '../utils/logger.js';

/**
 * useNotifications hook
 * Provides real-time notification data for the current user
 * 
 * @returns {Object} - { notifications, loading, error, unreadCount, refresh, markRead, markAllRead }
 */
export default function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);
  const mountedRef = useRef(true);

  // Calculate unread count
  const unreadCount = notifications.filter((n) => notificationsService.isNotificationUnread(n)).length;

  // Set up real-time listener
  const setupListener = useCallback(() => {
    
    if (!user) {
      setNotifications([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      unsubscribeRef.current = notificationsService.listenNotificationsForUser(
        user.uid,
        (rows) => {
          if (mountedRef.current) {
            setNotifications(rows || []);
            setLoading(false);
          }
        }
      );
    } catch (err) {
      if (mountedRef.current) {
        setError(err);
        setLoading(false);
      }
    }
  }, [user?.uid]);

  // Initial setup and cleanup
  useEffect(() => {
    mountedRef.current = true;
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    setupListener();

    return () => {
      mountedRef.current = false;
      if (unsubscribeRef.current) {
        try {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        } catch (err) {
          // Ignore cleanup errors
        errorDev("Operation failed:", err);
        }
      }
    };
  }, [setupListener]);

  // Refresh function to force re-fetch
  const refresh = useCallback(() => {
    if (unsubscribeRef.current) {
      try {
        unsubscribeRef.current();
      } catch (err) {
        // Ignore
      errorDev("Operation failed:", err);
      }
    }
    setupListener();
  }, [setupListener]);

  // Mark single notification as read
  const markRead = useCallback(async (notificationId) => {
    if (!notificationId) return;

    try {
      await notificationsService.markNotificationRead(notificationId);
      // Optimistic update
      setNotifications(prev =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, isRead: true, read: true } : n
        )
      );
    } catch (err) {
      // Silent fail - will sync via listener
    errorDev("Operation failed:", err);
    }
  }, []);

  // Mark all notifications as read
  const markAllRead = useCallback(async () => {
    if (!user) return;

    try {
      await notificationsService.markAllNotificationsRead(user.uid);
      // Optimistic update
      setNotifications(prev =>
        prev.map((n) => ({ ...n, isRead: true, read: true }))
      );
    } catch (err) {
      // Silent fail - will sync via listener
    errorDev("Operation failed:", err);
    }
  }, [user]);

  return {
    notifications,
    loading,
    error,
    unreadCount,
    refresh,
    markRead,
    markAllRead,
  };
}
